import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardContent, Typography } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import { useGetCapabilityQuery } from '../store/hooks';

export default function CapabilityDetailPage() {
  const navigate = useNavigate();
  const { capabilityId } = useParams<{ capabilityId: string }>();

  const {
    data: capability,
    isLoading,
    error,
  } = useGetCapabilityQuery({ capabilityId: capabilityId! }, { skip: !capabilityId });

  if (!capabilityId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid Capability ID</h2>
            <Button onClick={() => navigate('/admin-v2/capabilities')} className="mt-4">
              Back to Capabilities
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading capability..." />
        </main>
      </div>
    );
  }

  if (error || !capability) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading capability</h2>
            <p className="text-gray-600 mt-2">Unable to fetch capability details.</p>
            <Button onClick={() => navigate('/admin-v2/capabilities')} className="mt-4">
              Back to Capabilities
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/capabilities" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Capability Details</h1>
        </div>

        <Card>
          <CardContent>
            <div className="space-y-6">
              <div>
                <Typography variant="subtitle2" className="text-gray-500">
                  Name
                </Typography>
                <Typography variant="h6">{capability.name}</Typography>
              </div>

              <div>
                <Typography variant="subtitle2" className="text-gray-500">
                  Description
                </Typography>
                <Typography variant="body1">
                  {capability.description || (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </Typography>
              </div>

              <div>
                <Typography variant="subtitle2" className="text-gray-500">
                  Capability ID
                </Typography>
                <Typography variant="body2" className="font-mono text-gray-600">
                  {capability.capability_id}
                </Typography>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(new Date(capability.created_at))}
                  </Typography>
                  <Typography variant="body2" className="text-gray-500">
                    {formatRelativeTime(new Date(capability.created_at))}
                  </Typography>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(new Date(capability.updated_at))}
                  </Typography>
                  <Typography variant="body2" className="text-gray-500">
                    {formatRelativeTime(new Date(capability.updated_at))}
                  </Typography>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
